import { without } from 'lodash';
import Default from '../../../common/Themes/Default/Theme';
import { sectionHeadingAlignment, sectionHeadingHR } from '../../../common/utils/themeOverrides';
import * as boldIcons from '../../../common/IconPacks/boldOutline';
import * as socialIconPack from '../../../common/IconPacks/SocialIcons/defaultSocialIconPack';
import { Cascade } from '../../../common/loaders';
import { FILL, FIT, INSET, BLUR, LEGACY_BLUR } from '../../../common/constants/headerTreatments';
import { mergeTypographyOverrides } from '../../../common/utils/typography';
import themeConfig from '../themeConfig';
import { WIDGETS_WITH_CIRCULAR_IMAGES } from '../../../common/constants';

const typographyShared1 = {
  style: {
    font: 'primary',
    color: 'highlight',
    fontSize: 'xlarge',
    fontWeight: 'normal',
    letterSpacing: 'normal',
    textTransform: 'none'
  }
};

class Theme16 extends Default {
  static config = themeConfig;

  static get displayName() {
    return 'Theme16';
  }

  static excludedProps = without(Default.excludedProps, 'address').concat(['tagline2']);

  static getMutatorDefaultProps(widgetType, widgetPreset) {
    const defaultProps = super.getMutatorDefaultProps(widgetType, widgetPreset);
    const enableCircularImage =
      WIDGETS_WITH_CIRCULAR_IMAGES[widgetPreset] || defaultProps.enableCircularImage;

    return widgetType === 'HEADER'
      ? {
        ...defaultProps,
        phoneOnSecondaryPage: true,
        useSubtagline: false,
        headerTreatmentsConfig: {
          ...defaultProps.headerTreatmentsConfig,
          imageTreatments: {
            [FILL]: 'none',
            [FIT]: 'none',
            [INSET]: 'category-solid',
            [BLUR]: 'none',
            [LEGACY_BLUR]: 'category-overlay'
          },
          parallaxDisabledTreatments: [FIT]
        },
        useAddress: true,
        disableCTAList: true
      }
      : {
        ...defaultProps,
        enableCircularImage
      };
  }

  constructor() {
    super();
    this.mappedValues = {
      ...this.mappedValues,
      typographyOverrides: {
        LogoAlpha: {
          ...typographyShared1
        },
        HeadingAlpha: {
          style: {
            font: 'primary',
            color: 'highContrast',
            fontSize: 'xxlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingBeta: {
          ...typographyShared1
        },
        HeadingGamma: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'xlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingEpsilon: props => ({
          ...mergeTypographyOverrides.call(this, 'BodyBeta', props)
        }),
        NavAlpha: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'small',
            fontWeight: 'normal',
            letterSpacing: '0.071em',
            textTransform: 'none'
          }
        },
        SubNavAlpha: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'small',
            fontWeight: 'normal',
            letterSpacing: '0.071em',
            textTransform: 'none'
          }
        },
        ButtonAlpha: {
          style: {
            font: 'alternate',
            fontWeight: 'bold',
            letterSpacing: '0.071em',
            textTransform: 'none',
            ['@xs-only']: {
              fontSize: 'xsmall'
            }
          }
        }
      }
    };
  }

  getSection(inputSection) {
    if (inputSection === 'default') {
      return 'alt';
    } else if (inputSection === 'overlay') {
      return 'overlay';
    }

    return 'default';
  }

  Section(props) {
    return super.Section(
      this.merge(
        {
          style: {
            border: '0'
          }
        },
        props
      )
    );
  }

  Table(props) {
    return super.Table(
      this.merge(
        {
          style: {
            borderRadius: 'medium'
          }
        },
        props
      )
    );
  }

  Input(props) {
    const { section } = props;
    return super.Input(
      this.merge(
        {
          section: this.getSection(section || this.base.section),
          style: {
            borderColor: 'input',
            borderWidth: '0 0 3px 0',
            backgroundColor: 'inherit',
            borderBottomWidth: '3px',
            paddingHorizontal: 'small',
            paddingTop: 'small',
            borderRadius: '0',
            borderStyle: 'solid',
            paddingBottom: '11px'
          }
        },
        props
      )
    );
  }

  Icon(props) {
    return super.Icon(
      this.merge(
        {
          iconPack: { ...boldIcons, ...socialIconPack }
        },
        props
      )
    );
  }

  Loader(props) {
    return Cascade.apply(this, [props]);
  }

  SectionHeading({ layout = 'full', ...props }) {
    const base = this.base;
    const overrides = this.merge({}, sectionHeadingAlignment(base), sectionHeadingHR(base));
    return super.SectionHeading(
      this.merge(
        {
          style: {
            textAlign: 'center',
            ['@md']: {
              textAlign: layout === 'full' ? 'center' : 'left'
            }
          }
        },
        overrides,
        props
      )
    );
  }

  Pipe(props) {
    return super.Pipe(
      this.merge(
        {
          style: {
            backgroundColor: 'sectionLowContrast'
          }
        },
        props
      )
    );
  }

  InputTextArea(props) {
    return super.InputTextArea(
      this.merge(
        {
          rows: 5
        },
        props
      )
    );
  }

  InputSelect({ section, ...props }) {
    return super.InputSelect(
      this.merge(
        {
          section: this.getSection(section || this.base.section),
          style: {
            backgroundColor: 'section',
            borderColor: 'input',
            borderBottomWidth: 'medium',
            borderStyle: 'solid',
            borderRadius: 'none',
            ['@xs-only']: {
              fontSize: 'medium' // 16px
            }
          }
        },
        props
      )
    );
  }

  InputSelectElement(props) {
    return super.InputSelectElement(
      this.merge(
        {
          style: {
            paddingHorizontal: 'small',
            paddingTop: 'small',
            paddingBottom: '11px',
            ['@xs-only']: {
              fontSize: 'medium' // 16px
            }
          }
        },
        props
      )
    );
  }

  MediaObjectBackground(props) {
    return super.MediaObjectBackground(
      this.merge(
        {
          style: {
            borderRadius: 0
          }
        },
        props
      )
    );
  }

  Phone(props) {
    return super.Phone(
      this.merge(
        {
          typography: 'BodyAlpha',
          featured: true,
          style: {
            ':hover': {
              color: 'highlight'
            }
          }
        },
        props
      )
    );
  }

  Tagline(props) {
    return super.Tagline(
      this.merge(
        {
          style: {
            maxWidth: '100%',
            ['@md']: {
              marginBottom: '0 !important',
              marginHorizontal: 'medium'
            }
          },
          typography: 'HeadingEpsilon'
        },
        props
      )
    );
  }

  HeadingMajor(props) {
    return super.HeadingMajor(this.merge({ typography: 'HeadingAlpha' }, props));
  }

  Intro(props) {
    return super.Intro(this.merge({ alignment: 'center' }, props));
  }

  CardBannerHeading(props) {
    return super.CardBannerHeading(this.merge({ typography: 'HeadingAlpha' }, props));
  }

  MembershipLink(props) {
    return this.NavigationDrawerLink(
      this.merge(
        {
          style: {
            textTransform: 'uppercase',
            letterSpacing: 1
          }
        },
        props
      )
    );
  }

  ImageLogo(props) {
    return super.ImageLogo(
      this.merge(
        {
          style: {
            '@xs-only': {
              width: 'auto'
            }
          }
        },
        props
      )
    );
  }

  ContentCard(props) {
    const styles = this.base.widgetPreset === 'about1' ? { style: { alignItems: 'center' } } : {};
    return super.ContentCard(this.merge(styles, props));
  }

  ContactBarAddress(props) {
    return super.ContactBarAddress(
      this.merge(
        {
          style: {
            '@xs-only': {
              fontSize: 'small'
            }
          }
        },
        props
      )
    );
  }

  PromoBanner(props) {
    return super.PromoBanner(
      this.merge(
        {
          style: {
            paddingVertical: 'xsmall'
          }
        },
        props
      )
    );
  }

  PromoBannerText(props) {
    return super.PromoBannerText(
      this.merge(
        {
          style: {
            '@xs-only': {
              fontSize: 'small'
            }
          }
        },
        props
      )
    );
  }

  UtilitiesMenu({ pipe, ...props }) {
    return super.UtilitiesMenu(
      this.merge(
        {
          style: {
            '@md': {
              '> :first-child': {
                marginLeft: pipe ? 'medium' : '0' // override default theme
              }
            }
          }
        },
        props
      )
    );
  }
}

export default Theme16;
